import React from 'react'
import { Link } from 'react-router-dom'
import { imageUrl } from '../lib/images'
import SeoHead from '../components/SeoHead'
import WaveDivider from '../components/WaveDivider'
import { localizeTree } from '../i18n/localizeTree'

export default function OmMigPage({ lang = 'da' }) {
  return localizeTree((
    <>
      <SeoHead pageId="about" lang="da" />

      {/* Hero / Header Banner */}
      <section className="subpage-hero bg-[#F7ECEE] py-12 sm:py-16 border-b border-[#C68D94]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-script text-5xl sm:text-6xl text-[#3E3435] font-normal my-3">
            Et liv med chokolade
          </h1>
          <WaveDivider className="text-[#C68D94]/50 mx-auto mb-6" />
        </div>
      </section>

      {/* Main Story & Portraits */}
      <section className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-14 items-center mb-16">
          <div className="md:col-span-7 space-y-4 text-xs sm:text-sm text-[#3E3435]/90 font-light leading-relaxed">
            <p className="text-base sm:text-lg text-[#3E3435] font-normal leading-relaxed">
              Jeg hedder Karina, og jeg er kvinden bag Fanø Chokolade. For mig er chokolade mere end bare en sød fristelse – det er et håndværk, en passion og en kreativ leg med smag, farver og former.
            </p>
            <p>
              Det hele begyndte som en hobby, men i dag lever jeg af at skabe små, unikke smagsoplevelser, der både glæder øjet og ganen. I min café ved Fanø Bad byder jeg gæster velkommen til mit chokoladeunivers, hvor kvalitet og personlighed altid er i centrum.
            </p>
            <div className="pt-4 flex flex-col min-[667px]:flex-row items-start min-[667px]:items-center gap-4">
              <Link
                to="/aabningstider-og-adresse"
                className="bg-[#C68D94] hover:bg-[#A35D67] text-white px-6 py-3 rounded-full text-xs font-semibold tracking-wider uppercase transition-colors shadow-sm"
              >
                Besøg caféen
              </Link>
              <Link
                to="/om-mig#projektstoette"
                className="text-xs font-semibold uppercase tracking-wider text-[#A35D67] hover:underline"
              >
                Læs om EU LAG støtten →
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden border-4 border-white shadow-xl bg-stone-100">
              <img
                src={imageUrl(
                  '/_assets/fanoechokolade-003-scaled-e1757667075461.jpg',
                  'width=800,height=1000,fit=cover,quality=85,format=auto'
                )}
                alt="Karina, kvinden bag Fanø Chokolade"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 w-28 h-28 rounded-full border-2 border-[#C68D94]/30 pointer-events-none hidden sm:block" />
          </div>
        </div>

        {/* Min Rejse Timeline / Story Sections */}
        <div className="border-t border-[#C68D94]/20 pt-16">
          <div className="text-center max-w-xl mx-auto mb-12">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A35D67]">
              Historien bag
            </span>
            <h2 className="font-script text-4xl sm:text-5xl text-[#3E3435] font-normal my-2">
              Min rejse
            </h2>
            <WaveDivider className="text-[#C68D94]/40 mx-auto" />
            <p className="text-xs sm:text-sm text-[#3E3435]/80 font-light leading-relaxed mt-4">
              Jeg begyndte at lave chokolade hjemme i mit eget køkken – mest for sjov og af ren nysgerrighed. Det blev hurtigt mere end bare en hobby, for jeg fandt en kæmpe glæde i at fordybe mig i smage og udtryk.
            </p>
          </div>

          <div className="flex flex-col gap-10 lg:gap-14">
            {/* Step 1: De første skridt */}
            <div className="bg-white rounded-3xl p-8 sm:p-10 lg:p-12 border border-[#C68D94]/20 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 items-center">
                <div className="md:col-span-7 flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 rounded-full bg-[#F7ECEE] text-[#A35D67] flex items-center justify-center font-bold text-sm mb-4">
                      01
                    </div>
                    <h3 className="font-semibold text-lg sm:text-xl text-[#3E3435] mb-4">
                      De første skridt
                    </h3>
                    <div className="text-xs sm:text-sm text-[#3E3435]/85 font-light leading-relaxed space-y-3">
                      <p>
                        Jeg tog springet og blev selvstændig på fuld tid. Fanø Chokolade blev officielt en virksomhed, og jeg begyndte at sælge mine fyldte chokolader og andre små fristelser på lokale markeder og i udvalgte butikker.
                      </p>
                      <p>
                        Jeg påbegyndte en uddannelse som chocolatier for at styrke mit håndværk og få endnu mere viden om råvarer, teknikker og smagssammensætninger. Samtidig voksede efterspørgslen, og jeg begyndte at drømme om et fast sted, hvor jeg kunne kombinere produktion og café.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Column: Overlapping Circular Photography */}
                <div className="md:col-span-5 relative flex justify-center items-center py-6 lg:py-8">
                  {/* Primary Large Circle */}
                  <div className="aspect-square w-52 sm:w-64 md:w-60 lg:w-72 rounded-full overflow-hidden border-4 border-white shadow-xl bg-stone-100 relative z-10 shrink-0">
                    <img
                      src={imageUrl('/_assets/hx3a2499-scaled.jpg', 'width=800,height=800,fit=cover,quality=85,format=auto')}
                      alt="Håndlavede fyldte chokolader"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Secondary Overlapping Small Circle */}
                  <div className="absolute -bottom-2 -left-2 sm:bottom-0 sm:left-0 lg:bottom-2 lg:-left-2 aspect-square w-28 sm:w-36 md:w-32 lg:w-40 rounded-full overflow-hidden border-4 border-white shadow-lg bg-stone-100 z-20 shrink-0">
                    <img
                      src={imageUrl(
                        '/_assets/15stk-chokolade.jpg',
                        'width=500,height=500,fit=cover,quality=85,format=auto'
                      )}
                      alt="Fyldt chokolade æske"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2: Caféen åbner */}
            <div className="bg-white rounded-3xl p-8 sm:p-10 lg:p-12 border border-[#C68D94]/20 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 items-center">
                <div className="md:col-span-7 flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 rounded-full bg-[#F7ECEE] text-[#A35D67] flex items-center justify-center font-bold text-sm mb-4">
                      02
                    </div>
                    <h3 className="font-semibold text-lg sm:text-xl text-[#3E3435] mb-4">
                      Caféen åbner
                    </h3>
                    <div className="text-xs sm:text-sm text-[#3E3435]/85 font-light leading-relaxed space-y-3">
                      <p>
                        I maj åbnede jeg min butik og café på Strandvejen ved Fanø Bad. Her kan gæster følge med i chokoladeproduktionen, smage mine nyeste kreationer og nyde en kop kaffe, kage eller sæsonens fristelser i hyggelige omgivelser.
                      </p>
                      <p>
                        For mig er caféen mere end bare et sted at købe chokolade – det er et samlingspunkt, hvor jeg kan dele min passion med andre.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-stone-100 flex items-center gap-3">
                    <Link
                      to="/aabningstider-og-adresse"
                      className="text-xs font-semibold tracking-wider uppercase text-[#A35D67] hover:underline flex items-center gap-1.5"
                    >
                      <span>Besøg os på Strandvejen 58</span>
                      <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                    </Link>
                  </div>
                </div>

                {/* Right Column: Overlapping Circular Photography */}
                <div className="md:col-span-5 relative flex justify-center items-center py-6 lg:py-8">
                  {/* Primary Large Circle */}
                  <div className="aspect-square w-52 sm:w-64 md:w-60 lg:w-72 rounded-full overflow-hidden border-4 border-white shadow-xl bg-stone-100 relative z-10 shrink-0">
                    <img
                      src={imageUrl('/_assets/imgi-106-515206049-767625565591366-2309087864801483466-n.jpeg', 'width=800,height=800,fit=cover,quality=85,format=auto')}
                      alt="Udendørs terrasse ved Fanø Chokolade Café"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Secondary Overlapping Small Circle */}
                  <div className="absolute -bottom-2 -left-2 sm:bottom-0 sm:left-0 lg:bottom-2 lg:-left-2 aspect-square w-28 sm:w-36 md:w-32 lg:w-40 rounded-full overflow-hidden border-4 border-white shadow-lg bg-stone-100 z-20 shrink-0">
                    <img
                      src={imageUrl(
                        '/_assets/imgi-2-548634743-827060689647853-6046308657816260637-n.jpeg',
                        'width=500,height=500,fit=cover,quality=85,format=auto'
                      )}
                      alt="Gæster på caféterrassen"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Secondary photo: Karina with apron in the bakery */}
          <div className="mt-14 rounded-3xl overflow-hidden aspect-[16/9] max-h-96 w-full border border-[#C68D94]/20 shadow-sm">
            <img
              src={imageUrl(
                '/_assets/fanoechokolade-109-scaled-e1757666758293.jpg',
                'width=1400,quality=85,format=auto'
              )}
              alt="Karina i chokoladeværkstedet"
              className="w-full h-full object-cover object-[center_12%]"
            />
          </div>
        </div>

        <div id="projektstoette" className="mt-16 scroll-mt-28 bg-white rounded-3xl p-8 sm:p-12 border border-[#C68D94]/20 shadow-sm flex flex-col md:flex-row items-center gap-10">
          {/* Badge Image */}
          <div className="shrink-0 flex justify-center">
            <a
              href="https://www.livogland.dk/"
              target="_blank"
              rel="noopener noreferrer"
              className="group block p-4 bg-[#FDF8F7] border border-[#C68D94]/20 rounded-2xl hover:border-[#A35D67] transition-all shadow-xs"
              title="Besøg Liv og Land (åbner i nyt vindue)"
            >
              <img
                src={imageUrl('/_assets/blaa-tekst-vertikal-medfinansieret.png', 'width=350,quality=90,format=auto')}
                alt="Den Europæiske Landbrugsfond for Udvikling af Landdistrikterne"
                className="w-48 sm:w-56 h-auto object-contain transition-transform "
              />
            </a>
          </div>

          {/* Text Description verbatim from scrape */}
          <div className="text-xs sm:text-sm text-[#3E3435]/90 font-light leading-relaxed space-y-4">
            <p className="text-sm sm:text-base text-[#3E3435] font-normal">
              Fanø chokolade har modtaget EU midler til en ny chokolade tempererings maskine. I forbindelse med flytning af produktionen og udvidelse af samme har Fanø chokolade fået tilsagn og støtte til maskinen.
            </p>
            <p>
              Den har gjort det muligt at effektivisere chokolade fremstilling således at den nu imødekommer det behov der er for at fremstille mere chokolade mere effektivt.
            </p>
            <div className="pt-2">
              <a
                href="https://www.livogland.dk/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs font-semibold text-[#A35D67] hover:underline uppercase tracking-wider"
              >
                <span>Læs mere på Livogland.dk</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  ), lang)
}
